import type { FC } from "react";
import { memo } from "react";

import { languageSelector, useIdLikedContext } from "@/src/contexts";
import useAjax, { AjaxMethod, type Output } from "@Components/Hooks/useAjax";
import { onDefaultKeyboardUp, urlFormat } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import { Follow } from "@Routes/FollowLinks";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../styles/tokens.stylex";
import { LikeCounterResources } from "./LikeCounter.Resources";
import { LikeCounterResourcesEn } from "./LikeCounter.Resources.En";
import LikeBorder from "./icons/LikeBorder";

export interface ILikeCounter {
    id: string;
    likesCount: number;
    isSaved: boolean;
}

const areEqual = (prevProps: ILikeCounter, nextProps: ILikeCounter) =>
    prevProps.isSaved === nextProps.isSaved && prevProps.likesCount === nextProps.likesCount;

export const LikeCounter: FC<ILikeCounter> = memo((props) => {
    const [isSaved, saveId, unsaveId] = useIdLikedContext(props.id, "discounts");

    const { errorContainer, request } = useAjax<Output<boolean>>({
        ajaxAction: urlFormat(Follow, { action: "discounts" }),
        ajaxMethod: AjaxMethod.Post,
        success: (response: Output<boolean>) => {
            if (response?.success !== true) {
                return;
            }

            if (response.data) {
                saveId();
            } else {
                unsaveId();
            }
        },
    });

    const resources = languageSelector(LikeCounterResources, LikeCounterResourcesEn);

    let likesCount = props.likesCount;

    if (props.isSaved !== isSaved) {
        if (isSaved) likesCount++;
        else likesCount--;
    }

    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        request(undefined, { follow: !isSaved, id: props.id, origin: 0, likesCount });
    };

    return (
        <div>
            <button
                {...stylex.props(styles.likeClick)}
                onClick={onClick}
                type="button"
                onKeyUp={onDefaultKeyboardUp}
            >
                <LikeBorder {...stylex.props(styles.icon, isSaved && styles.iconActive)} />
                <Text fontWeight="bold" textElement="p">
                    {resources.coolResource}
                    <span {...stylex.props(styles.span, isSaved && styles.hide)}>?</span>
                </Text>
            </button>
            <Text textElement="p" styleX={styles.likeDescription}>
                {`${resources.counterDescription} ${likesCount}`}
            </Text>
            {errorContainer}
        </div>
    );
}, areEqual);

const styles = stylex.create({
    likeClick: {
        gap: 5,
        alignItems: "flex-end",
        color: colors.fg_Primary,
        display: "flex",
        width: "100%",
    },
    span: {
        transitionDuration: "200ms",
        transitionProperty: "opacity",
        transitionTimingFunction: "ease-out",
    },
    hide: {
        opacity: 0,
    },
    likeDescription: {
        color: colors.grayDark,
        fontSize: "0.6875rem",
        marginTop: 5,
    },
    icon: {
        fill: colors.grayDark,
        transform: { default: null, ":hover": "scale(1.1)" },
        transitionDuration: "200ms",
        transitionProperty: "fill,transform",
        transitionTimingFunction: "ease-out",
        height: 22,
        width: 22,
    },
    iconActive: {
        fill: colors.b_BrandMagenta,
    },
});
